import { AttackPattern } from '../AttackPattern.js';

/**
 * Twin Voice (The Antiphon, Act II) — the pattern that forces the polarity
 * choice. Two full rings fire together: one warm, one cold, the cold offset
 * by half the angular spacing so the two tones interleave into a single dense
 * wall. Whichever tone you are attuned to (dash to flip — see Player.attunement)
 * passes through you harmlessly; the other half of the wall still hits, and
 * hits harder (`mismatchBonus`). So there is no attunement that is safe against
 * the whole ring — you pick the tone of the bullets nearest you and dash to
 * dodge the rest, re-choosing as the gap rotates past.
 *
 * The only new idea here is the per-bullet `tone` (+ `mismatchBonus`) carried
 * on the pooled bullet; collision resolution lives in
 * SpawningSystem._checkPlayerCollisions. Everything else is a plain radial.
 */
export class TwinVoice extends AttackPattern {
  constructor({ minPhase = 0, config }) {
    super({ name: 'Twin Voice', telegraphDuration: 0.85, activeDuration: 0.4, minPhase });
    this.config = config;
    // This card writes its own bullet tones, so §B7's boss-level card tone
    // must not overwrite them (see SpawningSystem._overlayFor).
    this.tonesOwnBullets = true;
  }

  onActiveStart(boss, _player, pool) {
    const { ringCount, speed, damage, warmColor, coldColor, mismatchBonus, singleTone = null } = this.config;
    const spacing = (Math.PI * 2) / ringCount;
    const base = Math.random() * Math.PI * 2;
    // `singleTone` drops the quarrel to one voice — the Antiphon-Echo warden
    // (data/act2Bosses.js) is exactly that: half the fight, so a single
    // correct attunement genuinely is safe. Omitted by the Antiphon itself,
    // where the whole point is that no attunement ever is.
    if (singleTone) {
      const color = singleTone === 'cold' ? coldColor : warmColor;
      this._fireRing(pool, boss, ringCount, base, speed, damage, singleTone, color, mismatchBonus);
      return;
    }
    this._fireRing(pool, boss, ringCount, base, speed, damage, 'warm', warmColor, mismatchBonus);
    // Cold ring offset half a step so the two tones weave rather than overlap.
    this._fireRing(pool, boss, ringCount, base + spacing / 2, speed, damage, 'cold', coldColor, mismatchBonus);
  }

  _fireRing(pool, boss, count, baseAngle, speed, damage, tone, color, mismatchBonus) {
    for (let i = 0; i < count; i++) {
      const angle = baseAngle + (i / count) * Math.PI * 2;
      pool.fire({
        x: boss.x,
        y: boss.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        radius: 2.6,
        damage,
        color,
        lifetime: 5,
        tone,
        mismatchBonus,
      });
    }
  }
}
